import React, { Suspense, lazy, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import AnalyticsRouteTracker from './analytics/AnalyticsRouteTracker';
import { applyGoogleSiteVerification } from './seo/googleSiteVerification';
import { restoreConsentFromStorage } from './utils/cookieConsent';
import { useLenis } from './hooks/useLenis';
import { setPendingHomeScroll } from './utils/smoothScroll';
import { COLORS, FONTS } from './theme/designTokens';
import { LanguageProvider } from './context/LanguageContext';
import { IntroContext } from './context/IntroContext';
import { IntroHandoffProvider } from './context/IntroHandoffContext';
import { MenuProvider } from './context/MenuContext';
import CookieBanner from './components/CookieBanner';
import SitePasswordGate from './components/SitePasswordGate';
import MainLayout from './components/layout/MainLayout';
import HomePage from './pages/HomePage';

const SubPage = lazy(() => import('./pages/SubPage'));

const KontaktRedirect: React.FC = () => {
  setPendingHomeScroll('kontakt');
  return <Navigate to="/" replace />;
};

const theme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: COLORS.bg,
      paper: COLORS.bgWhite,
    },
    text: {
      primary: COLORS.text,
      secondary: COLORS.textMuted,
    },
  },
  typography: {
    fontFamily: FONTS.sans,
    h2: {
      textTransform: 'none',
    },
    body1: {
      fontWeight: 300,
      fontSize: '15px',
      lineHeight: '22px',
    },
  },
  shape: {
    borderRadius: 0,
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: 'none',
          boxShadow: 'none',
          backgroundColor: 'transparent',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
      },
    },
  },
});

const pageTransition = {
  exit: { opacity: 0 },
  transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] },
};

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div key={location.pathname} initial={false} animate={{ opacity: 1 }} {...pageTransition}>
        <Suspense fallback={null}>
          <Routes location={location}>
            <Route element={<MainLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/en" element={<HomePage />} />
              <Route path="/team/:slug" element={<SubPage kind="team-detail" />} />
              <Route path="/methodik/:slug" element={<SubPage kind="methodik-detail" />} />
              <Route path="/aktuelles" element={<SubPage kind="aktuelles" />} />
              <Route path="/aktuelles/:slug" element={<SubPage kind="aktuelles-detail" />} />
              <Route path="/kontakt" element={<KontaktRedirect />} />
              <Route path="/impressum" element={<SubPage kind="impressum" />} />
              <Route path="/datenschutz" element={<SubPage kind="datenschutz" />} />
              <Route path="/widerrufsbelehrung" element={<SubPage kind="widerruf" />} />
            </Route>
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

/** Hides the static hero headline once React has painted, and the photo on inner pages. */
const StaticHeroSync: React.FC = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const path = pathname.replace(/\/+$/, '') || '/';
    document.documentElement.classList.toggle('no-hero', path !== '/' && path !== '/en');
    document.documentElement.classList.add('app-ready');
  }, [pathname]);

  return null;
};

const AppShell: React.FC = () => {
  useLenis(true);

  return (
    <IntroContext.Provider value={true}>
      <IntroHandoffProvider>
        <StaticHeroSync />
        <AnimatedRoutes />
        <AnalyticsRouteTracker />
        <CookieBanner />
      </IntroHandoffProvider>
    </IntroContext.Provider>
  );
};

function App() {
  useEffect(() => {
    applyGoogleSiteVerification();
    restoreConsentFromStorage();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
        <SitePasswordGate>
          <BrowserRouter>
            <MenuProvider>
              <AppShell />
            </MenuProvider>
          </BrowserRouter>
        </SitePasswordGate>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
